import React, { useState } from 'react';
import './TradeChartView.css';
import TimelineChart from './charts/TimelineChart';
import CumulativePLChart from './charts/CumulativePLChart';
import WinRateChart from './charts/WinRateChart';
import AIAccuracyChart from './charts/AIAccuracyChart';
import PLWordCloud from './charts/PLWordCloud';
import PLTreemap from './charts/PLTreemap';
import CalendarHeatmap from './charts/CalendarHeatmap';
import TimeOfDayChart from './charts/TimeOfDayChart';

/**
 * Trade Chart View - Multi-chart visualization for trade records
 * Supports multiple chart types with dropdown selector
 */
const TradeChartView = ({ records, pairs = [] }) => {
  const [chartType, setChartType] = useState('timeline');

  const chartTypes = [
    { id: 'timeline', name: 'Timeline (Scatter)', icon: '📈', description: 'See trades over time' },
    { id: 'pl-wordcloud', name: 'P&L Word Cloud', icon: '☁️', description: 'Word size = P&L amount' },
    { id: 'pl-treemap', name: 'P&L Treemap', icon: '🗺️', description: 'Block size = P&L amount' },
    { id: 'cumulative-pl', name: 'Cumulative P&L', icon: '📉', description: 'Track P&L progression' },
    { id: 'win-rate', name: 'Win Rate by Stock', icon: '🎯', description: 'Compare win rates' },
    { id: 'ai-accuracy', name: 'AI Accuracy', icon: '🤖', description: 'Evaluate AI recommendations' },
    { id: 'calendar-heatmap', name: 'Calendar Heatmap', icon: '📅', description: 'P&L by date' },
    { id: 'time-of-day', name: 'Time of Day', icon: '⏰', description: 'P&L by trading hour' },
  ];

  const renderChart = () => {
    switch (chartType) {
      case 'timeline':
        return <TimelineChart records={records} pairs={pairs} />;
      case 'pl-wordcloud':
        return <PLWordCloud records={records} pairs={pairs} />;
      case 'pl-treemap':
        return <PLTreemap records={records} pairs={pairs} />;
      case 'cumulative-pl':
        return <CumulativePLChart records={records} pairs={pairs} />;
      case 'win-rate':
        return <WinRateChart records={records} pairs={pairs} />;
      case 'ai-accuracy':
        return <AIAccuracyChart records={records} pairs={pairs} />;
      case 'calendar-heatmap':
        return <CalendarHeatmap records={records} pairs={pairs} />;
      case 'time-of-day':
        return <TimeOfDayChart records={records} pairs={pairs} />;
      default:
        return <TimelineChart records={records} pairs={pairs} />;
    }
  };

  const currentChart = chartTypes.find(c => c.id === chartType);

  return (
    <div className="trade-chart-view">
      {/* Chart Type Selector */}
      <div className="chart-selector">
        <label className="chart-selector-label">Chart Type:</label>
        <select
          value={chartType}
          onChange={(e) => setChartType(e.target.value)}
          className="chart-selector-dropdown"
        >
          {chartTypes.map(chart => (
            <option key={chart.id} value={chart.id}>
              {chart.icon} {chart.name}
            </option>
          ))}
        </select>
        {currentChart && (
          <span className="chart-description">{currentChart.description}</span>
        )}
      </div>

      {/* Chart Container */}
      <div className="chart-container">
        {renderChart()}
      </div>

      {/* Legend/Help */}
      <div className="chart-legend">
        {chartType === 'timeline' && (
          <>
            <span className="legend-item">
              <span className="legend-dot-solid"></span> Entry (solid)
            </span>
            <span className="legend-item">
              <span className="legend-dot-hollow"></span> Exit (hollow)
            </span>
            <span className="legend-item">
              <span className="legend-line-dashed"></span> Pair Connection
            </span>
            <span className="legend-hint">Color = Stock | Size = Quantity | Dashed line = FIFO paired trades</span>
          </>
        )}
        {(chartType === 'pl-wordcloud' || chartType === 'pl-treemap') && (
          <span className="legend-hint">
            <span style={{ color: '#22c55e', fontWeight: 'bold' }}>Green</span> = Profit |
            <span style={{ color: '#ef4444', fontWeight: 'bold' }}> Red</span> = Loss |
            Larger = Higher P&L
          </span>
        )}
        {chartType === 'cumulative-pl' && (
          <span className="legend-hint">Shows running total of P&L over time. Green = profit zone.</span>
        )}
        {chartType === 'win-rate' && (
          <span className="legend-hint">Green &gt;70% | Yellow 50-70% | Red &lt;50%</span>
        )}
        {chartType === 'ai-accuracy' && (
          <span className="legend-hint">Compares AI-recommended trades vs manual trades performance.</span>
        )}
        {chartType === 'calendar-heatmap' && (
          <span className="legend-hint">Darker color = Higher P&L | Green = Profit | Red = Loss</span>
        )}
        {chartType === 'time-of-day' && (
          <span className="legend-hint">Shows P&L by trading hour. Find your best time to trade.</span>
        )}
      </div>
    </div>
  );
};

export default TradeChartView;
